// Staff copy for the Settings frame and its building blocks (admin/settingsKit.tsx).
// English only (redesign 2026-10): az and ru stay empty and fall back to English.
import type { Phrase } from '../staffText'

const en = (text: string): Phrase => ({ en: text, az: '', ru: '' })

export const settings = {
  // Menu groups (pages/Administration.tsx GROUPS).
  'settings.group.organisation': en('Organisation'),
  'settings.group.people': en('Users'),
  'settings.group.branches': en('Branches'),
  'settings.group.queues': en('Queues'),
  'settings.group.booking': en('Online booking'),
  'settings.group.screens': en('Screens'),
  'settings.group.communication': en('Communication'),
  'settings.group.connections': en('Connections'),
  'settings.group.installation': en('Installation'),

  // Shared words of the kit.
  'settings.optional': en('(optional)'),
  'settings.saveChanges': en('Save changes'),
  // The save bar, when the page counts its changes.
  'settings.changes': en('{n} change|{n} changes'),
  // A save is live: a machine not yet saved, a removal, branches a profile save could not reach.
  'settings.live.notSaved': en('Not saved'),
  'settings.live.removed': en('Removed.'),
  'settings.live.notLive': en('Saved, not live at {names}'),
  'settings.crumbs': en('Breadcrumb'),

  // General (admin/Options.tsx).
  'settings.general': en('General'),
  'settings.general.line': en('How your organisation works.'),

  // Queues group: services, queues, calling, equipment, operation profiles, screen apps.
  'settings.all': en('All'),
  'settings.col.branches': en('Branches'),
  'settings.services.line': en('What customers come to do.'),
  'settings.services.count': en('{n} service|{n} services'),
  'settings.services.off': en('Not offered'),
  'settings.queues.line': en('Where customers wait.'),
  'settings.queues.count': en('{n} queue|{n} queues'),
  'settings.queues.col.queue': en('Queue'),
  'settings.queues.col.numbers': en('Numbers'),
  'settings.queues.col.target': en('Target'),
  'settings.calling.line': en('The order desks call queues in.'),
  'settings.calling.list': en('Calling profiles'),
  'settings.calling.count': en('{n} profile|{n} profiles'),
  'settings.calling.levels': en('{n} level|{n} levels'),
  'settings.calling.col.profile': en('Profile'),
  'settings.calling.col.calls': en('Calls'),
  'settings.equipment.count': en('{n} profile|{n} profiles'),
  'settings.equipment.find': en('Search profiles'),
  'settings.equipment.col.profile': en('Profile'),
  'settings.equipment.band': en('Equipment profile'),
  'settings.equipment.details': en('Details'),
  'settings.equipment.detailsLine': en('Its name and a note.'),
  'settings.equipment.units': en('Units'),
  'settings.equipment.deleteTitle': en('Delete profile'),
  'settings.equipment.deleteLine': en('Only when no branch uses it.'),
  'settings.op.line': en('Queues, groups and who calls them.'),
  'settings.op.count': en('{n} profile|{n} profiles'),
  'settings.op.find': en('Search profiles'),
  'settings.op.band': en('Operation profile'),
  'settings.op.queues': en('Queues'),
  'settings.op.groups': en('Groups'),
  'settings.op.deleteTitle': en('Delete profile'),
  'settings.op.deleteLine': en('Only when no branch uses it.'),
  'settings.apps.line': en('Your own kiosk and board apps.'),
  'settings.apps.list': en('Apps'),
  'settings.apps.find': en('Search apps'),
  'settings.apps.col.app': en('App'),
  'settings.apps.col.version': en('Version'),
  'settings.apps.col.machines': en('Machines'),

  // Roles (admin/Roles.tsx).
  'settings.roles.line': en('What each role can open.'),
  'settings.roles.count': en('{n} role|{n} roles'),
  'settings.roles.find': en('Search roles'),
  'settings.roles.all': en('All'),
  'settings.roles.builtIn': en('Built in'),
  'settings.roles.custom': en('Custom'),
  'settings.roles.col.type': en('Type'),

  // Branch management and one branch's page (admin/Branches.tsx, BranchPage.tsx).
  'settings.branches.line': en('Your branches, their hours and desks.'),
  'settings.branches.list': en('Branches'),
  'settings.branches.all': en('All'),
  'settings.branches.openNow': en('Open now'),
  'settings.branches.off': en('Out of service'),
  'settings.branches.col.branch': en('Branch'),
  'settings.branches.col.profile': en('Profile'),
  'settings.branches.col.status': en('Status'),
  'settings.branch.band': en('Branch'),
  'settings.branch.setupLine': en('Profiles, hours and the business day.'),
  'settings.branch.equipmentLine': en('Counters, kiosks and screens here.'),
  'settings.branch.desks': en('Desks'),
  'settings.branch.queues': en('Queues'),
  'settings.branch.waiting': en('Waiting'),
  'settings.branch.resetTitle': en('Reset the day'),
  'settings.branch.resetLine': en('Closes open tickets. Numbers restart.'),
} satisfies Record<`settings.${string}`, Phrase>
